import type { ShopperBasketsTypes, ShopperProductsTypes } from 'commerce-sdk-isomorphic'

export type Promotion = {
  link: string
  promotionId: string
  calloutMsg?: string
  promotionalPrice?: number
}

export type ConstructorRecommendedProduct = {
  data: object
  isSlotted: boolean
  labels: object
  matchedTerms: string[]
  resultId: string
  strategy: { id: string }
  value: string
  variations: object[]
}

export const mapProductToDataLayer = (
  selectedVariant: Partial<Omit<FlattenedProduct, 'id'>>,
  locale: Locales,
  options?: ProductDataLayerOptions,
): ProductDataLayer => {
  const {
    brand,
    id,
    name,
    sku,
    c_bvAverageRating: rating,
    c_coreSize,
    c_isSaleEvent,
    c_metalColour,
    c_pcpEligibleProduct,
    c_PIMColourPWBRings,
    c_PIMColourGold,
    c_ringSize,
    c_saleEvent,
    inventory,
    price,
    tieredPrices,
    variationAttributes,
    variationValues,
  } = selectedVariant

  const badgeText = c_isSaleEvent ? [c_saleEvent] : []

  const stockStatus = getStockStatus(inventory?.stockLevel ?? 0)

  const variant = variationValues ? Object.values(variationValues).join('|') : undefined

  const goldColor =
    variationAttributes && getCustomAttributeDataLayer(variationAttributes, 'PIMColourGold', c_PIMColourGold)
  const pwbRingsColor =
    variationAttributes && getCustomAttributeDataLayer(variationAttributes, 'PIMColourPWBRings', c_PIMColourPWBRings)

  const wishlisted = options?.wishlisted || false

  const pcpOption =
    c_pcpEligibleProduct &&
    selectedVariant?.options
      ?.find((option: ShopperProductsTypes.Option) => option.id === 'pcp')
      ?.values?.find(
        (value: ShopperProductsTypes.OptionValue) =>
          value.id ===
          options?.optionItems?.find((option: ShopperBasketsTypes.OptionItem) => option?.optionId === 'pcp')
            ?.optionValueId,
      )

  const pcpType = c_pcpEligibleProduct && pcpOption ? mapPcpDatalayerName(pcpOption.id) : 'n/a'

  const pcpPrice = (c_pcpEligibleProduct && pcpOption ? pcpOption?.price : 0) ?? 0

  const mappedPrices = mapPrices(tieredPrices ?? [])

  const productAssociations = productAssociationsForDataLayer(options?.productAssociations)
  // Omit the key entirely when there are no associations, rather than emitting an empty value.
  const associationParams: { productAssociations?: typeof productAssociations } = productAssociations
    ? { productAssociations }
    : {}

  return {
    name,
    id,
    price,
    brand: brand || 'Michael Hill',
    category: getCategoryDatalayer(selectedVariant),
    quantity: options?.quantity ?? 1,
    position: (options?.position ?? 0) + 1,
    list: options?.list ?? undefined,
    variant,
    sku: sku || selectedVariant?.id,
    size: options?.selectedRingSize ?? c_ringSize ?? c_coreSize,
    colour: c_metalColour ?? goldColor ?? pwbRingsColor,
    originalPrice: mappedPrices?.retailPrice ?? undefined,
    cartSource: options?.cartSource,
    memberPrice: mappedPrices?.memberPrice ?? price,
    rating,
    imageUrl: findPrimaryAngle(selectedVariant.images) || selectedVariant?.image?.url,
    url: getProductUrl({ productId: id, productName: name, locale }),
    salePrice: mappedPrices?.doublePrice,
    badges: badgeText,
    engraved: options?.engravingValue ? 'yes' : 'no',
    stockStatus,
    wishlisted: wishlisted ?? undefined,
    pcpType,
    pcpPrice,
    priceAfterItemDiscount: undefined, // unable to get this info with this context as calculated in cart productItem
    priceAfterOrderDiscount: undefined, // unable to get this info with this context as calculated in cart productItem
    ...associationParams,
  }
}

// To map product line item to data layer for basket and order
export const mapProductLineItemToDataLayer = (
  productItem: ShopperBasketsTypes.ProductItem,
  wishlist: FlattenedWishlist,
  locale: Locales,
) => {
  const {
    brand,
    inventory,
    price,
    priceAfterItemDiscount,
    priceAfterOrderDiscount,
    productId,
    productName: name,
    productCustomAttributes,
    productPrices,
    image,
    quantity,
    variant: productVariant,
    variationAttributes,
    c_ringSize,
  } = productItem

  const badgeText = productCustomAttributes?.c_isSaleEvent ? [productCustomAttributes.c_saleEvent] : []

  const stockStatus = getStockStatus(inventory?.stockLevel ?? 0)

  const variant = productVariant.variationValues ? Object.values(productVariant.variationValues).join('|') : undefined

  const goldColor = getCustomAttributeDataLayer(
    variationAttributes,
    'PIMColourGold',
    productCustomAttributes?.c_PIMColourGold,
  )
  const pwbRingsColor = getCustomAttributeDataLayer(
    variationAttributes,
    'PIMColourPWBRings',
    productCustomAttributes?.c_PIMColourPWBRings,
  )

  const wishlisted = wishlist?.some((wishlistItem: FlattenedWishlistItem) => wishlistItem.productId === productId)

  const engravingOption =
    productCustomAttributes?.c_engravable &&
    productItem.optionItems?.find((option) => option?.optionId === 'engraving')?.optionValueId

  const engraved = mapEngravingDatalayerName(engravingOption)

  const pcpOption =
    productCustomAttributes?.c_pcpEligibleProduct &&
    productItem?.optionItems?.find((option: ShopperBasketsTypes.OptionItem) => option.optionId === 'pcp')

  const pcpType = productCustomAttributes?.c_pcpEligibleProduct ? mapPcpDatalayerName(pcpOption?.optionValueId) : 'n/a'

  const pcpPrice = productCustomAttributes?.c_pcpEligibleProduct ? pcpOption?.price : 0

  const mappedPrices = mapPrices(productPrices ?? [])

  return {
    id: productId ?? '',
    name,
    price,
    brand,
    category: getCategoryDatalayer(productCustomAttributes),
    quantity,
    variant,
    sku: productId,
    size: c_ringSize ?? productCustomAttributes?.c_coreSize,
    colour: productCustomAttributes?.c_metalColour ?? goldColor ?? pwbRingsColor,
    originalPrice: mappedPrices?.retailPrice,
    cartSource: undefined,
    memberPrice: mappedPrices?.memberPrice ?? price,
    rating: productCustomAttributes?.c_bvRatingRange,
    imageUrl: image?.url,
    url: getProductUrl({ productId, productName: name, locale }),
    salePrice: mappedPrices?.doublePrice,
    badges: badgeText,
    engraved,
    stockStatus,
    wishlisted,
    pcpType,
    pcpPrice,
    priceAfterItemDiscount,
    priceAfterOrderDiscount,
  }
}

/**
 * Retrieves the name of a custom attribute from a list of attributes for Data layer purposes.
 *
 * @param {Array} attributes - The list of attributes to search through.
 * @param {string} id - The ID of the attribute to find.
 * @param {string} customAttributeName - The name of the custom attribute to find.
 * @returns {string | undefined} The name of the custom attribute if found, otherwise undefined.
 */
export const getCustomAttributeDataLayer = (
  attributes: ShopperProductsTypes.VariationAttribute[],
  id: string,
  customAttributeName: string,
) => {
  return attributes
    ?.find((f: ShopperProductsTypes.VariationAttribute) => {
      return f.id === id
    })
    ?.values?.find((v: ShopperProductsTypes.VariationAttributeValue) => {
      return v.value === customAttributeName
    })?.name
}

/**
 * Retrieves the category data for a given product and returns it in the way the datalayer expects
 *
 * @param {Object} product - The product object containing custom attributes.
 * @returns {string | undefined} The category data layer string in the format "PrimaryCategory/SubCategory" or "RetailCategory/SubCategory", or undefined if custom attributes are not present.
 */
export const getCategoryDatalayer = (product: Partial<FlattenedProduct>) => {
  const primary = product?.c_mHJRetailCategory || product?.c_mHJPrimaryCategory
  if (!primary && !product?.c_mHJSubCategory) return undefined

  return `${primary}/${product?.c_mHJSubCategory}`
}

export const mapPcpDatalayerName = (optionId: string) => {
  if (optionId?.includes('PCP001')) return '3 year'
  if (optionId?.includes('PCP003')) return '10 year'
  return 'none'
}

export const mapEngravingDatalayerName = (optionId: string) => {
  if (optionId === '2' || optionId === '3') return 'yes'
  return 'no'
}

export const getStockStatus = (stockLevel: number) => {
  return stockLevel > 0 ? 'In stock' : 'Out of stock'
}
